import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    BarChart2, Plus, Play, RefreshCcw, X, Trash2, GitCompare, ChevronRight,
    CheckCircle2, XCircle, AlertCircle, MinusCircle, Clock, DollarSign, Database,
    Rocket,
} from "lucide-react";

const STATUS_BADGE = {
    pass: "text-emerald-400",
    fail: "text-red-400",
    errored: "text-amber-400",
    unknown: "text-white/40",
    running: "text-amber-400",
    queued: "text-white/40",
    completed: "text-emerald-400",
    failed: "text-red-400",
};

export default function AgentEvals({ agentId, agent }) {
    const [datasets, setDatasets] = useState([]);
    const [runs, setRuns] = useState([]);
    const [testCases, setTestCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);
    const [editingDs, setEditingDs] = useState(null);
    const [detail, setDetail] = useState(null);
    const [compare, setCompare] = useState(null); // {leftId, rightId, data}
    const [selectedRuns, setSelectedRuns] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [d, r, tc] = await Promise.all([
                api.get(`/admin/agents/${agentId}/datasets`),
                api.get(`/admin/agents/${agentId}/eval-runs?limit=100`),
                api.get(`/admin/agents/${agentId}/testcases`),
            ]);
            setDatasets(d.data.items || []);
            setRuns(r.data.items || []);
            setTestCases(tc.data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const triggerRun = async (datasetId) => {
        try {
            const { data } = await api.post(`/admin/agents/${agentId}/datasets/${datasetId}/run`, { parallelism: 3 });
            toast.success("Eval run started · polling…");
            // Poll until completed
            const pollId = data.id;
            const poll = async () => {
                try {
                    const { data: r } = await api.get(`/admin/agents/${agentId}/eval-runs/${pollId}`);
                    if (r.status === "running" || r.status === "queued") {
                        setTimeout(poll, 3000);
                        return;
                    }
                    toast.success(`Eval ${r.status} · ${r.totals.passed}/${r.totals.total} passed`);
                    load();
                } catch {
                    load();
                }
            };
            setTimeout(poll, 3000);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const removeDataset = async (id) => {
        if (!window.confirm("Delete this dataset? Eval runs created from it remain.")) return;
        try {
            await api.delete(`/admin/agents/${agentId}/datasets/${id}`);
            toast.success("Dataset deleted");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const showDetail = async (runId) => {
        const { data } = await api.get(`/admin/agents/${agentId}/eval-runs/${runId}`);
        setDetail(data);
    };

    const toggleSelectRun = (runId) => {
        if (selectedRuns.includes(runId)) {
            setSelectedRuns(selectedRuns.filter((id) => id !== runId));
        } else if (selectedRuns.length >= 2) {
            setSelectedRuns([selectedRuns[1], runId]); // keep last 2
        } else {
            setSelectedRuns([...selectedRuns, runId]);
        }
    };

    const runCompare = async () => {
        if (selectedRuns.length !== 2) return toast("Select exactly 2 runs to compare");
        const [leftId, rightId] = selectedRuns;
        try {
            const { data } = await api.get(`/admin/agents/${agentId}/eval-runs/${leftId}/compare/${rightId}`);
            setCompare({ leftId, rightId, data });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="mt-5 space-y-5" data-testid="agent-evals">
            {/* Datasets */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <Database className="w-4 h-4 text-[#7C3AED]" />
                            <h2 className="font-display text-lg tracking-tight">Evaluation datasets</h2>
                        </div>
                        <div className="text-xs text-white/50 mt-0.5">Group test cases for batch regression. Each dataset can be run against any version.</div>
                    </div>
                    <button onClick={() => setOpenCreate(true)} data-testid="evals-create-dataset"
                        className="px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#6D28D9] flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5" /> New dataset
                    </button>
                </div>
                <div className="border border-white/10 bg-[#0a0a0a] rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-white/[0.02] border-b border-white/10">
                            <tr className="text-left label-xs text-white/40">
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Size</th>
                                <th className="px-4 py-3">Tags</th>
                                <th className="px-4 py-3">Updated</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">Loading…</td></tr>
                            ) : datasets.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40" data-testid="evals-empty">No datasets yet — create one to start regression testing.</td></tr>
                            ) : datasets.map((d) => (
                                <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{d.name}</div>
                                        <div className="text-[10px] text-white/40">{d.description || "—"}</div>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs">{d.test_case_ids?.length || 0} TCs</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {(d.tags || []).map((t) => (
                                                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/60">{t}</span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-[10px] text-white/40">{new Date(d.updated_at).toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <button onClick={() => triggerRun(d.id)} title="Run dataset"
                                                data-testid={`evals-run-${d.id}`}
                                                className="px-2 py-1 bg-emerald-400/10 border border-emerald-400/30 text-emerald-400 rounded-md text-[10px] font-medium hover:bg-emerald-400/20 flex items-center gap-1">
                                                <Play className="w-3 h-3" /> Run
                                            </button>
                                            <button onClick={() => setEditingDs(d)} title="Edit"
                                                data-testid={`evals-edit-${d.id}`}
                                                className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white">
                                                <RefreshCcw className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => removeDataset(d.id)} title="Delete"
                                                className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Eval runs history */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <BarChart2 className="w-4 h-4 text-[#7C3AED]" />
                            <h2 className="font-display text-lg tracking-tight">Evaluation runs</h2>
                        </div>
                        <div className="text-xs text-white/50 mt-0.5">
                            {selectedRuns.length === 0 && "Select 2 runs to compare versions."}
                            {selectedRuns.length === 1 && "Select 1 more run to compare."}
                            {selectedRuns.length === 2 && (
                                <span className="text-[#7C3AED]">2 runs selected — ready to compare.</span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load}
                            className="px-3 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5 flex items-center gap-1.5">
                            <RefreshCcw className="w-3.5 h-3.5" /> Refresh
                        </button>
                        <button
                            onClick={runCompare}
                            disabled={selectedRuns.length !== 2}
                            data-testid="evals-compare"
                            className={`px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                                selectedRuns.length === 2
                                    ? "bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
                                    : "bg-white/5 text-white/40 cursor-not-allowed"
                            }`}>
                            <GitCompare className="w-3.5 h-3.5" /> Compare versions
                        </button>
                    </div>
                </div>
                <div className="border border-white/10 bg-[#0a0a0a] rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-white/[0.02] border-b border-white/10">
                            <tr className="text-left label-xs text-white/40">
                                <th className="px-4 py-3 w-8"></th>
                                <th className="px-4 py-3">Dataset</th>
                                <th className="px-4 py-3">v</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Pass / Total</th>
                                <th className="px-4 py-3">Avg latency</th>
                                <th className="px-4 py-3">Total cost</th>
                                <th className="px-4 py-3">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.length === 0 ? (
                                <tr><td colSpan={8} className="px-4 py-8 text-center text-white/40">No eval runs yet.</td></tr>
                            ) : runs.map((r) => {
                                const passed = r.totals?.passed || 0;
                                const total = r.totals?.total || 0;
                                const ratio = total > 0 ? passed / total : 0;
                                return (
                                    <tr key={r.id}
                                        data-testid={`evalrun-row-${r.id}`}
                                        className={`border-b border-white/5 cursor-pointer ${selectedRuns.includes(r.id) ? "bg-[#7C3AED]/10" : "hover:bg-white/[0.02]"}`}
                                        onClick={() => showDetail(r.id)}
                                    >
                                        <td className="px-4 py-3">
                                            <input type="checkbox" checked={selectedRuns.includes(r.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => toggleSelectRun(r.id)}
                                                data-testid={`evalrun-select-${r.id}`} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{r.dataset_name}</div>
                                            <div className="font-mono text-[10px] text-white/40">{r.id.slice(0, 8)}…</div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs">v{r.version_no}</td>
                                        <td className="px-4 py-3"><span className={`text-[11px] font-mono ${STATUS_BADGE[r.status] || ""}`}>{r.status}</span></td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="font-mono text-xs">{passed}/{total}</div>
                                                <div className="flex-1 max-w-[80px] h-1.5 bg-white/5 rounded-full overflow-hidden">
                                                    <div className={`h-full ${ratio === 1 ? "bg-emerald-400" : ratio >= 0.7 ? "bg-[#7C3AED]" : "bg-red-400"}`}
                                                        style={{ width: `${ratio * 100}%` }} />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs">{r.aggregates?.avg_latency_ms || 0} ms</td>
                                        <td className="px-4 py-3 font-mono text-xs">${(r.aggregates?.total_cost_usd || 0).toFixed(5)}</td>
                                        <td className="px-4 py-3 font-mono text-[10px] text-white/40">{new Date(r.created_at).toLocaleString()}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {openCreate && (
                <DatasetEditor
                    agentId={agentId}
                    testCases={testCases}
                    onClose={() => setOpenCreate(false)}
                    onSaved={() => { setOpenCreate(false); load(); }}
                />
            )}
            {editingDs && (
                <DatasetEditor
                    agentId={agentId}
                    testCases={testCases}
                    dataset={editingDs}
                    onClose={() => setEditingDs(null)}
                    onSaved={() => { setEditingDs(null); load(); }}
                />
            )}
            {detail && <EvalRunModal run={detail} onClose={() => setDetail(null)} />}
            {compare && <CompareModal compare={compare} agentId={agentId} agent={agent} onClose={() => setCompare(null)} />}
        </div>
    );
}

function DatasetEditor({ agentId, testCases, dataset, onClose, onSaved }) {
    const isEdit = !!dataset;
    const [name, setName] = useState(dataset?.name || "");
    const [description, setDescription] = useState(dataset?.description || "");
    const [tags, setTags] = useState((dataset?.tags || []).join(", "));
    const [selectedIds, setSelectedIds] = useState(dataset?.test_case_ids || []);
    const [busy, setBusy] = useState(false);

    const toggle = (id) => {
        setSelectedIds(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error("Name required");
        setBusy(true);
        try {
            const body = {
                name: name.trim(),
                description: description.trim() || null,
                test_case_ids: selectedIds,
                tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
            };
            if (isEdit) {
                await api.patch(`/admin/agents/${agentId}/datasets/${dataset.id}`, body);
            } else {
                await api.post(`/admin/agents/${agentId}/datasets`, body);
            }
            toast.success(isEdit ? "Dataset updated" : "Dataset created");
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="dataset-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <Database className="w-4 h-4 text-[#7C3AED] mr-2" />
                    <div className="font-display text-lg tracking-tight">{isEdit ? "Edit dataset" : "New dataset"}</div>
                    <button onClick={onClose} className="ml-auto text-white/60"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <div className="label-xs mb-1.5">Name *</div>
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="dataset-name" className="input" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Description</div>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Tags (comma-separated)</div>
                        <input value={tags} onChange={(e) => setTags(e.target.value)} className="input" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5 flex items-center justify-between">
                            <span>Test cases ({selectedIds.length} selected)</span>
                            {selectedIds.length === 0 && <span className="text-amber-400 normal-case tracking-normal">Select at least 1 test case</span>}
                        </div>
                        <div className="border border-white/10 rounded-md max-h-72 overflow-y-auto bg-[#050505]">
                            {testCases.length === 0 ? (
                                <div className="p-5 text-center text-xs text-white/40">No test cases yet — create some on the Test cases tab first.</div>
                            ) : testCases.map((tc) => (
                                <label key={tc.id} className="flex items-start gap-2 px-3 py-2 hover:bg-white/[0.02] cursor-pointer border-b border-white/5">
                                    <input type="checkbox" checked={selectedIds.includes(tc.id)} onChange={() => toggle(tc.id)}
                                        data-testid={`dataset-tc-${tc.id}`} className="mt-1" />
                                    <div className="flex-1">
                                        <div className="text-sm">{tc.name}</div>
                                        <div className="text-[10px] text-white/40">{tc.expected_output ? "✓ has expected_output" : "no expected_output (will be unknown)"}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                        <button type="submit" disabled={busy || selectedIds.length === 0} data-testid="dataset-submit"
                            className="flex-1 bg-[#7C3AED] text-white py-2 rounded-md text-xs font-medium hover:bg-[#6D28D9] disabled:opacity-50">
                            {busy ? "Saving…" : isEdit ? "Save changes" : "Create dataset"}
                        </button>
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function EvalRunModal({ run, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="evalrun-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <BarChart2 className="w-4 h-4 text-[#7C3AED] mr-2" />
                    <div className="font-display text-lg tracking-tight">{run.dataset_name} · v{run.version_no}</div>
                    <span className={`ml-3 text-[10px] uppercase px-2 py-0.5 rounded-full border ${run.status === "completed" ? "border-emerald-400/30 text-emerald-400" : "border-amber-400/30 text-amber-400"}`}>{run.status}</span>
                    <button onClick={onClose} className="ml-auto text-white/60"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-5 gap-3">
                        <Stat label="Passed" value={run.totals?.passed || 0} color="text-emerald-400" />
                        <Stat label="Failed" value={run.totals?.failed || 0} color="text-red-400" />
                        <Stat label="Errored" value={run.totals?.errored || 0} color="text-amber-400" />
                        <Stat label="Unknown" value={run.totals?.unknown || 0} color="text-white/40" />
                        <Stat label="Total" value={run.totals?.total || 0} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Stat label="Avg latency" value={`${run.aggregates?.avg_latency_ms || 0} ms`} icon={<Clock className="w-3 h-3" />} />
                        <Stat label="Total cost" value={`$${(run.aggregates?.total_cost_usd || 0).toFixed(5)}`} icon={<DollarSign className="w-3 h-3" />} />
                        <Stat label="Tokens" value={`${run.aggregates?.total_tokens_in || 0} / ${run.aggregates?.total_tokens_out || 0}`} />
                    </div>
                    {run.error && <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md p-3">{run.error}</div>}
                    <div className="border border-white/10 rounded-md overflow-hidden" data-testid="evalrun-items">
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.02] border-b border-white/10">
                                <tr className="text-left label-xs text-white/40">
                                    <th className="px-3 py-2">Test case</th>
                                    <th className="px-3 py-2">Result</th>
                                    <th className="px-3 py-2">Latency</th>
                                    <th className="px-3 py-2">Cost</th>
                                    <th className="px-3 py-2">Why / Output</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(run.items || []).map((it) => (
                                    <tr key={it.test_case_id} className="border-b border-white/5 align-top">
                                        <td className="px-3 py-2 text-xs">{it.test_case_name || it.test_case_id.slice(0, 8)}</td>
                                        <td className="px-3 py-2"><ResultPill status={it.status} /></td>
                                        <td className="px-3 py-2 font-mono text-xs">{it.latency_ms || 0} ms</td>
                                        <td className="px-3 py-2 font-mono text-xs">${(it.cost_usd || 0).toFixed(5)}</td>
                                        <td className="px-3 py-2 font-mono text-[10px] text-white/60 max-w-md">
                                            {(it.assertion_failures || []).length > 0 ? (
                                                <ul className="space-y-1 text-rose-300/85 list-disc list-inside" data-testid={`eval-fail-${it.test_case_id}`}>
                                                    {(it.assertion_failures || []).slice(0, 6).map((msg, i) => (
                                                        <li key={i} className="break-words">{msg}</li>
                                                    ))}
                                                    {(it.assertion_failures || []).length > 6 && (
                                                        <li className="text-white/40">+ {it.assertion_failures.length - 6} more …</li>
                                                    )}
                                                </ul>
                                            ) : it.error ? (
                                                <span className="text-rose-300/85">{it.error}</span>
                                            ) : (
                                                <span className="text-white/45 truncate inline-block max-w-md">
                                                    {it.actual_output ? JSON.stringify(it.actual_output).slice(0, 120) : "—"}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CompareModal({ compare, agentId, agent, onClose }) {
    const { data } = compare;
    const tr = data.transitions || {};
    const [promoting, setPromoting] = useState(false);

    const right = data.right || {};
    const isWinner =
        (tr.fail_to_pass || 0) > (tr.pass_to_fail || 0) &&
        (tr.pass_to_fail || 0) === 0 &&
        right.version_no &&
        right.version_no !== agent?.active_version_no;

    const promote = async () => {
        if (!isWinner) return;
        if (!window.confirm(
            `Promote v${right.version_no} to active for "${agent.name}"?\n\n` +
            `This will:\n` +
            `  · point active_version_no at v${right.version_no}\n` +
            `  · copy v${right.version_no}'s prompts/schemas/config back to the agent\n` +
            (agent.status === "draft" ? `  · auto-elevate status from 'draft' to 'active'\n` : "") +
            `\nAny flow node referencing this agent (without pinning) will pick up the new version on its next run.`,
        )) return;
        setPromoting(true);
        try {
            await api.post(`/admin/agents/${agentId}/versions/${right.version_no}/promote`);
            toast.success(`Promoted v${right.version_no} to active`);
            onClose();
            // best-effort full reload of the page so the editor reflects the new active version
            setTimeout(() => window.location.reload(), 600);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setPromoting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="compare-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <GitCompare className="w-4 h-4 text-[#7C3AED] mr-2" />
                    <div className="font-display text-lg tracking-tight">v{data.left.version_no} → v{data.right.version_no}</div>
                    {isWinner && (
                        <button
                            onClick={promote}
                            disabled={promoting}
                            data-testid="compare-promote"
                            className="ml-3 px-3 py-1.5 bg-emerald-400/10 border border-emerald-400/40 text-emerald-400 rounded-md text-xs font-medium hover:bg-emerald-400/20 disabled:opacity-50 flex items-center gap-1.5"
                            title={`No regressions, ${tr.fail_to_pass} test(s) gained → ready to ship`}
                        >
                            <Rocket className="w-3.5 h-3.5" /> {promoting ? "Promoting…" : `Promote v${right.version_no} to active`}
                        </button>
                    )}
                    <button onClick={onClose} className="ml-auto text-white/60"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-5 gap-3">
                        <Stat label="↗ Pass→Fail (regressions)" value={tr.pass_to_fail || 0} color="text-red-400" />
                        <Stat label="↘ Fail→Pass (gains)" value={tr.fail_to_pass || 0} color="text-emerald-400" />
                        <Stat label="= Same pass" value={tr.same_pass || 0} color="text-emerald-400/60" />
                        <Stat label="= Same fail" value={tr.same_fail || 0} color="text-red-400/60" />
                        <Stat label="? Other" value={tr.other || 0} color="text-white/40" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <RunSummaryCard side="LEFT" run={data.left} />
                        <RunSummaryCard side="RIGHT" run={data.right} />
                    </div>
                    <div className="border border-white/10 rounded-md overflow-hidden" data-testid="compare-rows">
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.02] border-b border-white/10">
                                <tr className="text-left label-xs text-white/40">
                                    <th className="px-3 py-2">Test case</th>
                                    <th className="px-3 py-2">v{data.left.version_no}</th>
                                    <th className="px-3 py-2">v{data.right.version_no}</th>
                                    <th className="px-3 py-2">Δ latency</th>
                                    <th className="px-3 py-2">Δ cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.rows || []).map((row) => (
                                    <tr key={row.test_case_id} className="border-b border-white/5">
                                        <td className="px-3 py-2 text-xs">{row.test_case_name || row.test_case_id.slice(0, 8)}</td>
                                        <td className="px-3 py-2"><ResultPill status={row.left.status} /></td>
                                        <td className="px-3 py-2"><ResultPill status={row.right.status} /></td>
                                        <td className={`px-3 py-2 font-mono text-xs ${row.delta_latency_ms > 0 ? "text-red-400" : row.delta_latency_ms < 0 ? "text-emerald-400" : "text-white/40"}`}>
                                            {row.delta_latency_ms > 0 ? "+" : ""}{row.delta_latency_ms} ms
                                        </td>
                                        <td className={`px-3 py-2 font-mono text-xs ${row.delta_cost_usd > 0 ? "text-red-400" : row.delta_cost_usd < 0 ? "text-emerald-400" : "text-white/40"}`}>
                                            {row.delta_cost_usd > 0 ? "+" : ""}${row.delta_cost_usd.toFixed(5)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RunSummaryCard({ side, run }) {
    return (
        <div className="border border-white/10 rounded-md p-3 bg-[#050505]">
            <div className="flex items-center justify-between mb-2">
                <div className="label-xs">{side} · v{run.version_no}</div>
                <div className="font-mono text-[10px] text-white/40">{new Date(run.created_at).toLocaleString()}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-emerald-400 font-mono">{run.totals?.passed}</span> passed</div>
                <div><span className="text-red-400 font-mono">{run.totals?.failed}</span> failed</div>
                <div><span className="text-amber-400 font-mono">{run.totals?.errored}</span> errored</div>
                <div><span className="text-white/40 font-mono">{run.totals?.unknown}</span> unknown</div>
                <div className="col-span-2 text-white/60">avg <span className="font-mono">{run.aggregates?.avg_latency_ms} ms</span> · cost <span className="font-mono">${(run.aggregates?.total_cost_usd || 0).toFixed(5)}</span></div>
            </div>
        </div>
    );
}

function ResultPill({ status }) {
    const map = {
        pass: { icon: CheckCircle2, c: "text-emerald-400" },
        fail: { icon: XCircle, c: "text-red-400" },
        errored: { icon: AlertCircle, c: "text-amber-400" },
        unknown: { icon: MinusCircle, c: "text-white/40" },
    };
    const M = map[status] || map.unknown;
    const Icon = M.icon;
    return (
        <span className={`text-[11px] font-mono inline-flex items-center gap-1 ${M.c}`}>
            <Icon className="w-3 h-3" /> {status || "—"}
        </span>
    );
}

function Stat({ label, value, icon, color }) {
    return (
        <div className="border border-white/10 rounded-md p-3 bg-[#050505]">
            <div className="label-xs text-white/40 flex items-center gap-1.5">{icon} {label}</div>
            <div className={`font-mono text-sm mt-0.5 ${color || ""}`}>{value}</div>
        </div>
    );
}
